// Hooks do CONTRATO: catálogo de planos, assinatura da marca, contato de
// cobrança e as duas portas para o Stripe. Mesmo padrão de use-dispositions.ts:
//   - `src/api/plans.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam nas cinco telas (Plano, Pagamentos,
// degrau de assinatura, faixa de carência e exclusão de marca), string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, e uma
// chave que divergisse uma letra viraria dado velho na tela, sem erro nenhum.
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBilling } from "@/api/costs";
import {
  getPlans,
  getSubscription,
  getSubscriptionAccess,
  openBillingPortal,
  setBillingContact,
  startCheckout,
} from "@/api/plans";

// As chaves DESTE domínio. As duas ressalvas que importam:
//
//  - `access` (["subscription", "access"]) é FILHA de `subscription`
//    (["subscription"]), e isso é o mecanismo, não um acidente: invalidar a
//    assinatura derruba junto o estado do gate, que é a mesma verdade vista com
//    menos campos. É por isso que `useDeleteCallRecording` (use-calls.ts)
//    invalidando ["subscription"] também refaz a leitura da faixa de carência.
//  - `brandDeactivationBilling` fica FORA daquele prefixo, apesar de ler a
//    assinatura: é uma pergunta com resposta derivada ("há dinheiro em aberto
//    que impeça excluir esta marca?"), chaveada pela ORG, e cara — dois GETs.
//    Dentro de ["subscription", …] toda mutação de cobrança a refaria em
//    background numa tela de configurações que nem está aberta. Divergência
//    pré-existente da tela de exclusão de marca; preservada.
export const planKeys = {
  plans: ["plans"] as const,
  subscription: ["subscription"] as const,
  access: ["subscription", "access"] as const,
  brandDeactivationBilling: (orgId: string) => ["brand-deactivation-billing", orgId] as const,
};

// --- Leituras ---

// Catálogo de planos. `enabled` é o gate de gestão: para o vendedor esta rota
// responde 403, e pedir seria gastar um request para receber um erro previsível.
export function usePlans(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: planKeys.plans,
    queryFn: getPlans,
    enabled: options.enabled,
  });
}

// A assinatura da marca, com plano, contato de cobrança e o consumo corrente.
export function useSubscription() {
  return useQuery({
    queryKey: planKeys.subscription,
    queryFn: getSubscription,
  });
}

// Estado do gate para QUALQUER membro (a rota pobre — sem plano nem preço).
export function useSubscriptionAccess() {
  return useQuery({
    queryKey: planKeys.access,
    queryFn: getSubscriptionAccess,
  });
}

// Há cobrança viva que impeça excluir a marca? Duas leituras de módulos
// DIFERENTES (contrato + medição) colapsadas num booleano e numa fatura: é uma
// pergunta da tela, não do back, e por isso mora no hook — `src/api/plans.ts`
// não importa `src/api/costs.ts` e continua não importando.
//
// `retry: false` é da tela e vem junto: a pré-checagem é UX, não autorização.
// Falhando a leitura, o botão continua disponível e o DELETE do back decide.
export function useBrandDeactivationBilling(orgId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: planKeys.brandDeactivationBilling(orgId),
    queryFn: async () => {
      const [assinatura, faturas] = await Promise.all([getSubscription(), getBilling(12)]);
      const assinaturaViva = assinatura.hasStripeSubscription && assinatura.status !== "canceled";
      const faturaAberta = faturas.find(
        (fatura) =>
          fatura.invoice &&
          (fatura.invoice.status === "open" || fatura.invoice.status === "uncollectible"),
      );
      return {
        assinaturaViva,
        faturaAberta: faturaAberta?.invoice ?? null,
      };
    },
    enabled: options.enabled,
    retry: false,
  });
}

// --- Mutações ---

// Derruba a assinatura (e, pelo prefixo, o estado do gate). `useCallback` e não
// uma seta solta: a identidade estável é o que permite passá-la adiante (ou
// pô-la num array de dependência) sem recriar a cadeia a cada render.
export function useInvalidateSubscription() {
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries({ queryKey: planKeys.subscription }),
    [queryClient],
  );
}

// Contato de cobrança (email + telefone). Os `mutationFn` daqui chamam a função
// de api dentro de uma seta: o TanStack invoca o mutationFn com (variáveis,
// contexto), e uma função de api que aceite um segundo parâmetro receberia o
// contexto como se fosse dado.
export function useSetBillingContact(
  handlers: {
    onSuccess?: (result: {
      billingEmail: string | null;
      billingPhone: string | null;
      billingRecipient: string | null;
    }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const invalidate = useInvalidateSubscription();
  return useMutation({
    mutationFn: (contato: { billingEmail: string | null; billingPhone: string | null }) =>
      setBillingContact(contato),
    onSuccess: (result) => {
      handlers.onSuccess?.(result);
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Contratar/trocar de plano. Sem invalidação de propósito: o sucesso é sair do
// app para o Stripe, e quem volta volta por uma navegação nova — invalidar aqui
// derrubaria cache para uma árvore que está sendo desmontada.
export function useStartCheckout(
  handlers: {
    onSuccess?: (result: { url: string }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: (planSlug: string) => startCheckout(planSlug),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

// Customer Portal do Stripe. Mesma forma (e mesmo motivo) do checkout acima.
export function useOpenBillingPortal(
  handlers: {
    onSuccess?: (result: { url: string }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: () => openBillingPortal(),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}
